import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import VerificationBanner from "../components/VerificationBanner";
import axios from "axios";
import { toast } from "sonner";
import { FileText, MessageSquare, Clock, Upload, Plus, ArrowRight, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const Dashboard = () => {
  const { token, user, refreshUser } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteContract, setDeleteContract] = useState(null);
  
  // Debug logging
  console.log('[Dashboard] user object:', user);
  console.log('[Dashboard] email_verified:', user?.email_verified);

  useEffect(() => {
    fetchData();
    // Refresh user data on mount to get latest verification status
    if (refreshUser) {
      refreshUser();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, questionsRes] = await Promise.all([
        axios.get(`${API}/contracts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/questions/history?limit=5`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setContracts(contractsRes.data);
      setRecentQuestions(questionsRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!deleteContract) return;
    try {
      await axios.delete(`${API}/contracts/${deleteContract.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(contracts.filter(c => c.id !== deleteContract.id));
      toast.success("Contract deleted");
    } catch (error) {
      toast.error("Failed to delete contract");
    } finally {
      setDeleteContract(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Verification Banner */}
        <VerificationBanner user={user} />
        
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">Manage your contracts and get answers</p>
        </div>

        {/* Quick Actions */}
        <div className="bento-grid mb-8">
          <Link
            to="/upload"
            className="bg-card border border-border rounded-md p-6 card-interactive flex items-start gap-4"
            data-testid="upload-card"
          >
            <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Upload className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-medium mb-1">Upload Contract</h3>
              <p className="text-sm text-muted-foreground">Add a new CBA (PDF, DOCX, or image)</p>
            </div>
          </Link>

          {contracts.length > 0 && (
            <Link
              to={`/ask/${contracts[0].id}`}
              className="bg-card border border-border rounded-md p-6 card-interactive flex items-start gap-4"
              data-testid="ask-question-card"
            >
              <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Ask a Question</h3>
                <p className="text-sm text-muted-foreground">Get guidance on your most recent contract</p>
              </div>
            </Link>
          )}

          <Link
            to="/history"
            className="bg-card border border-border rounded-md p-6 card-interactive flex items-start gap-4"
            data-testid="history-card"
          >
            <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-medium mb-1">View History</h3>
              <p className="text-sm text-muted-foreground">Browse your previous questions</p>
            </div>
          </Link>
        </div>

        {/* Contracts */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold">Your Contracts</h2>
            <Link to="/upload">
              <Button variant="outline" size="sm" data-testid="add-contract-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Contract
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-20 skeleton" />
              ))}
            </div>
          ) : contracts.length === 0 ? (
            <div className="empty-state bg-card border border-border rounded-md">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">No contracts yet</h3>
              <p className="empty-state-description">Upload your first Collective Bargaining Agreement to get started</p>
              <Link to="/upload" className="mt-4">
                <Button data-testid="first-upload-btn">Upload Contract</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  className="bg-card border border-border rounded-md p-4 flex items-center justify-between"
                  data-testid={`contract-item-${contract.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium">{contract.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {contract.chunk_count} sections • {formatDate(contract.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/ask/${contract.id}`}>
                      <Button variant="outline" size="sm" data-testid={`ask-btn-${contract.id}`}>
                        Ask <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteContract(contract)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-btn-${contract.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Questions */}
        {recentQuestions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Recent Questions</h2>
              <Link to="/history" className="text-sm text-primary link-underline">View all</Link>
            </div>
            <div className="space-y-3">
              {recentQuestions.map((q) => (
                <div
                  key={q.id}
                  className="bg-card border border-border rounded-md p-4"
                  data-testid={`recent-question-${q.id}`}
                >
                  <p className="font-medium mb-2 line-clamp-1">{q.question}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className={`risk-badge risk-${q.risk_level.toLowerCase()}`}>
                      {q.risk_level} Risk
                    </span>
                    <span>{formatDate(q.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteContract} onOpenChange={() => setDeleteContract(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contract?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteContract?.name}" and all associated questions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContract}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default Dashboard;
